import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import {
  getTalentComments,
  postAddTalentComment,
  postRemoveTalentComment,
  postUpdateTalentComment,
  TalentComment,
} from "../../talents/talentAPI";
import { TalentIdQueryParameter } from "../../talents/types/queries";
import { RootState } from "../../../app/store";
import {
  AddTalentComment,
  RemoveTalentComment,
  UpdateTalentComment,
} from "../../talents/types/commands";
import { EventPayload } from "../../../common/types/Event";
import {
  TalentCommentAdded,
  TalentCommentUpdated,
} from "../../talents/types/events";

export type SliceState = {
  ids: string[];
  entities: {};
  status: "idle" | "pending" | "succeeded" | "failed";
};

const talentCommentsAdapter = createEntityAdapter<TalentComment>({
  sortComparer: (a, b) => b.dateTime - a.dateTime,
});

export const fetchTalentComments = createAsyncThunk<
  TalentComment[],
  TalentIdQueryParameter,
  { state: RootState }
>(
  "talents/fetchTalentComments",
  async (filters: TalentIdQueryParameter, { requestId }) =>
    await getTalentComments(filters, { requestId }),
);

export const addTalentComment = createAsyncThunk(
  "talents/addTalentComment",
  async (
    comment: AddTalentComment,
    { requestId },
  ): Promise<EventPayload<TalentCommentAdded>> =>
    await postAddTalentComment(comment, { requestId }),
);

export const updateTalentComment = createAsyncThunk(
  "talents/updateTalentComment",
  async (
    comment: UpdateTalentComment,
    { requestId },
  ): Promise<EventPayload<TalentCommentUpdated>> =>
    await postUpdateTalentComment(comment, { requestId }),
);

export const removeTalentComment = createAsyncThunk(
  "talents/removeTalentComment",
  async (
    comment: RemoveTalentComment,
    { requestId },
  ): Promise<EventPayload<{}>> =>
    await postRemoveTalentComment(comment, { requestId }),
);

export const talentCommentsSlice = createSlice({
  name: "talentComments",
  initialState: talentCommentsAdapter.getInitialState({
    status: "idle",
  }) as SliceState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchTalentComments.fulfilled, (state, action) => {
      talentCommentsAdapter.setAll(state, action.payload);
      state.status = "succeeded";
    });
    builder.addCase(fetchTalentComments.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchTalentComments.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(addTalentComment.fulfilled, (state, action) => {
      talentCommentsAdapter.upsertOne(state, {
        id: action.payload.id,
        version: action.payload.version,
        ...action.payload.payload,
      });
    });
    builder.addCase(updateTalentComment.fulfilled, (state, action) => {
      talentCommentsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: {
          version: action.payload.version,
          ...action.payload.payload,
        },
      });
    });
    builder.addCase(removeTalentComment.fulfilled, (state, action) => {
      talentCommentsAdapter.removeOne(state, action.meta.arg.id);
    });
  },
});

export const { selectAll: selectAllTalentComments } =
  talentCommentsAdapter.getSelectors(
    (state: RootState) => state.talentComments,
  );

export const talentCommentsReducer = talentCommentsSlice.reducer;
